import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./treemapDrillTable.module.css";
import ZeroData from "../../zeroData/zeroData";
import loadingGif from "../../../assets/loading.gif";
import { educationConsolidatedData } from "../../../services/education";

const PAGE_SIZE = 10;

// Tabela que aparece abaixo dos treemaps ao clicar num bloco. Mostra as linhas
// consolidadas já filtradas pelo docente/disciplina selecionado.
// filter: { field, operator, values, display, sourceTitle }
const TreemapDrillTable = ({ institutionId, filter, onClose }) => {
  const navigate = useNavigate();
  const { field, operator, values, display, sourceTitle } = filter;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);

  // Volta pra página 1 sempre que muda o filtro (bloco/campo clicado).
  useEffect(() => {
    setPage(1);
  }, [field, display]);

  const fetchRows = useCallback(async () => {
    if (!institutionId || !field || !values?.length) {
      return;
    }
    try {
      setLoading(true);
      const response = await educationConsolidatedData({
        institutionId,
        page,
        pageSize: PAGE_SIZE,
        filters: [{ field, operator, values, groupId: 0 }]
      });
      const data = response?.educationData;
      setRows(data?.educationConsolidatedData || []);
      setTotalPages(data?.totalPages || 1);
      setTotalResults(data?.totalResults || 0);
    } catch (error) {
      console.error("Erro ao carregar dados do bloco:", error);
      setRows([]);
      setTotalPages(1);
      setTotalResults(0);
    } finally {
      setLoading(false);
    }
  }, [institutionId, field, operator, values, page]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  // Abre a tela completa de dados consolidados com o mesmo filtro (filtros avançados, export, etc.).
  const openFullPage = () => {
    const params = new URLSearchParams();
    params.set("page", "1");
    params.set("filter0_field", field);
    params.set("filter0_operator", operator);
    params.set("filter0_values", JSON.stringify(values));
    params.set("filter0_groupId", "0");
    navigate(`/app/dados-consolidados-academicos?${params.toString()}`);
  };

  const grade = (v) => (v === null || v === undefined ? "-" : Number(v).toFixed(2));

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h2>{sourceTitle}</h2>
          <span className={styles.subtitle}>
            Filtrando por <strong>{display}</strong>
            {totalResults > 0 && ` — ${totalResults} registro(s)`}
          </span>
        </div>
        <div className={styles.headerActions}>
          <button type="button" className={styles.linkBtn} onClick={openFullPage}>
            Abrir em dados consolidados
          </button>
          <button type="button" className={styles.closeBtn} onClick={onClose} title="Fechar">
            ×
          </button>
        </div>
      </div>

      {loading ? (
        <div className={styles.loading}>
          <img src={loadingGif} alt="Carregando..." />
        </div>
      ) : !rows.length ? (
        <ZeroData>
          <h4>Nenhum registro para este filtro.</h4>
        </ZeroData>
      ) : (
        <>
          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>ID do Aluno</th>
                  <th>Curso</th>
                  <th>Disciplina</th>
                  <th>Nota</th>
                  <th>Faltas</th>
                  <th>Status</th>
                  <th>Período</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr key={idx}>
                    <td>{r.StudentId || "-"}</td>
                    <td>{r.CourseTitle || "-"}</td>
                    <td>{r.Discipline || "-"}</td>
                    <td>{grade(r.Grade)}</td>
                    <td>{r.Absences ?? "-"}</td>
                    <td>{r.Status || "-"}</td>
                    <td>{r.AcademicPeriod || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                ‹
              </button>
              <span className={styles.pageInfo}>
                Página {page} de {totalPages}
              </span>
              <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                ›
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TreemapDrillTable;
